import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, Plus, Loader2, X, Clock, CheckCircle, XCircle,
  Send, Eye, EyeOff, FileText
} from 'lucide-react';
import { studentCorruptionAPI } from '../../services/api';

const STATUS_CONFIG = {
  pending: { label: 'Jarayonda', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  accepted: { label: 'Qabul qilindi', color: '#22c55e', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rad qilindi', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
};

const StudentCorruption = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    involved_person: '',
    is_anonymous: false,
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await studentCorruptionAPI.getAll();
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await studentCorruptionAPI.create(formData);
      setShowModal(false);
      setFormData({
        title: '',
        description: '',
        location: '',
        involved_person: '',
        is_anonymous: false,
      });
      fetchReports();
    } catch (error) {
      alert(error.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = (report) => {
    setSelectedReport(report);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><AlertTriangle size={28} /> Korrupsiya xabarlari</h1>
          <p>Korrupsiya holatlari haqida xabar bering</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={18} />
          Yangi xabar
        </button>
      </header>

      {/* Info banner */}
      <div className="corruption-info-banner">
        <div className="info-icon">
          <AlertTriangle size={32} />
        </div>
        <div className="info-content">
          <h3>Korrupsiyaga qarshi kurash!</h3>
          <p>
            Agar siz korrupsiya holati (pora, zo'ravonlik, adolatsizlik) haqida bilsangiz, 
            iltimos xabar bering. Xabaringiz maxfiy saqlanadi. Anonim yuborishingiz mumkin.
          </p>
        </div>
      </div>

      {/* Mening xabarlarim */}
      {reports.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} />
          <p>Siz hali xabar yubormadingiz</p>
        </div>
      ) : (
        <div className="corruption-reports-list">
          <h3>Mening xabarlarim</h3>
          {reports.map(report => {
            const statusConfig = STATUS_CONFIG[report.status];
            const StatusIcon = statusConfig.icon;
            return (
              <div 
                key={report.id} 
                className="corruption-report-card"
                onClick={() => openDetail(report)}
              >
                <div className="report-status-badge" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                  <StatusIcon size={14} />
                  {statusConfig.label}
                </div>
                <h4>{report.title}</h4>
                <p className="report-preview">{report.description.substring(0, 150)}...</p>
                <div className="report-meta">
                  <span>{new Date(report.created_at).toLocaleDateString('uz-UZ')}</span>
                  {report.is_anonymous && (
                    <span className="anonymous-badge">
                      <EyeOff size={14} />
                      Anonim
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Yangi xabar modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yangi xabar yuborish</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Sarlavha *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Qisqacha sarlavha"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Batafsil tavsif *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Voqeani batafsil tasvirlab bering..."
                    rows={5}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Qayerda sodir bo'ldi?</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Masalan: 205-xona, kutubxona"
                    />
                  </div>
                  <div className="form-group">
                    <label>Kim tomonidan?</label>
                    <input
                      type="text"
                      value={formData.involved_person}
                      onChange={(e) => setFormData({...formData, involved_person: e.target.value})}
                      placeholder="Lavozim yoki ism"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_anonymous}
                      onChange={(e) => setFormData({...formData, is_anonymous: e.target.checked})}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="checkbox-text">
                      <strong>Anonim yuborish</strong>
                      <span>Ismingiz va ma'lumotlaringiz ko'rinmaydi</span>
                    </div>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? <Loader2 className="spinner" size={18} /> : <Send size={18} />}
                  Yuborish
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selectedReport && (
        <div className="modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedReport.title}</h2>
              <button className="modal-close" onClick={() => setSelectedReport(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="report-detail-status">
                {(() => {
                  const config = STATUS_CONFIG[selectedReport.status];
                  const Icon = config.icon;
                  return (
                    <span style={{ background: config.bg, color: config.color }}>
                      <Icon size={16} />
                      {config.label}
                    </span>
                  );
                })()}
              </div>
              
              <div className="report-detail-section">
                <h4>Tavsif:</h4>
                <p>{selectedReport.description}</p>
              </div>
              
              {selectedReport.location && (
                <div className="report-detail-section">
                  <h4>Joylashuv:</h4>
                  <p>{selectedReport.location}</p>
                </div>
              )}
              
              {selectedReport.involved_person && (
                <div className="report-detail-section">
                  <h4>Ayblanuvchi:</h4>
                  <p>{selectedReport.involved_person}</p>
                </div>
              )}
              
              {selectedReport.admin_comment && (
                <div className="report-detail-section admin-response">
                  <h4>Admin javobi:</h4>
                  <p>{selectedReport.admin_comment}</p>
                </div>
              )}
              
              <div className="report-detail-meta">
                <span>Yuborilgan: {new Date(selectedReport.created_at).toLocaleString('uz-UZ')}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedReport(null)}>
                Yopish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCorruption;
